import { useDebounce } from "hooks/use-debounce";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "api";

import { T_Api_Paginated, T_Api_Success_Res } from "./api.types";
import {
  Creditor_Filter,
  DebtorFilter,
  DraftDto,
  EmailAccountConfig,
  EmailThread,
  EmailThreadData,
  ProfileACL,
  ProfileAclCapabilities,
  ProfileAclTargets,
  ProfileDto,
  ProfileType,
} from "dtos/email.dto";
import { Dayjs } from "dayjs";
import { T_MongoId } from "types/_utils.types";
import { AprChart } from "dtos/apr-chart.dto";
import { EmailType } from "components/EmailDashboard/email-sidebar";

export type ManualAuthRequest = {
  name: string;
  email: string;
  appPassword: string;
  authType: string;
  provider: ProfileType;
};
export interface SendEmailPayload {
  to?: string[]; // list of recipient emails
  cc?: string[]; // list of CC emails
  bcc?: string[]; // list of BCC emails
  subject: string; // email subject
  body: string; // HTML body content
  attachments: File[];
}

export interface PaginationProps {
  page: number;
  limit: number;
  search?: string;
  type?: EmailType;
  matchEmails?: string[];
  tags?: string[];
  orgCode: string | undefined;
  appKey: string | undefined;
  loanId: string | undefined;
  profileId: string | undefined;
}

export const emailService = {
  useSendEmail: () => {
    return useMutation({
      mutationKey: ["send-email"],
      mutationFn: async (payload: {
        to: string[];
        subject: string;
        body: string;
        appKey: string;
        cc: string[];
        bcc: string[];
        from?: string;
        loan: string | undefined;
        communication_type?: string;
        attachments: File[];
        negotiation_id: string | null;
      }) => {
        const formData = new FormData();
        for (const f of payload.attachments) {
          formData.append("files", f);
        }

        formData.set("subject", payload.subject);
        formData.set("body", payload.body);
        formData.set("to", JSON.stringify(payload.to));
        if (payload.loan) {
          formData.set("loan", payload.loan);
        }
        if (payload.from) {
          formData.set("from", payload.from);
        }
        if (payload.cc.length > 0) {
          formData.set("cc", JSON.stringify(payload.cc));
        }
        if (payload.bcc.length > 0) {
          formData.set("bcc", JSON.stringify(payload.bcc));
        }
        if (payload.communication_type) {
          formData.set("communication_type", JSON.stringify(payload.communication_type));
        }
        if (payload.negotiation_id) {
          formData.set("negotiation_id", payload.negotiation_id);
        }

        const res = await api.post(`/mail/send?appKey=${payload.appKey}`, formData);

        return res.data;
      },
    });
  },

  useInitiateOAuth: () => {
    return useMutation({
      mutationKey: ["initiate/oauth"],
      mutationFn: async ({ appKey, provider }: { provider: ProfileType; appKey: string }) => {
        const { data } = await api.post<
          T_Api_Success_Res<{
            data: { link: string };
            type: ProfileType;
          }>
        >(
          `/mail/link/${provider}?appKey=${appKey}`,
          {
            provider,
          },
          { params: { callbackUrl: encodeURIComponent(globalThis.location.href) } },
        );

        if (!data.result.data?.link) {
          throw new Error("Failed to link account");
        }

        return data.result;
      },
      onSuccess: (data) => {
        globalThis.location.href = data.data.link;
      },
    });
  },
  useManualAuth: () => {
    return useMutation({
      mutationFn: async ({
        appKey,
        credentials,
        orgCode,
      }: {
        credentials: ManualAuthRequest;
        appKey: string | undefined;
        orgCode?: string;
      }) => {
        let url = "";
        if (appKey) {
          url = `/mail/credentials?appKey=${appKey}&verifySmtp=true&verifyImap=true`;
        } else if (orgCode) {
          url = `/organisation/profile?orgCode=${orgCode}&verifySmtp=true&verifyImap=true`;
        }

        const { name, email, ...payload } = credentials;

        const { data } = await api.post(url, { entries: [{ name, email }], ...payload });
        return data.result;
      },
    });
  },

  useMarkThreadAsRead: () => {
    const queryClient = useQueryClient();

    return (threadId: string) => {
      return queryClient.setQueriesData(
        { queryKey: ["emails"], exact: false },
        (oldData: T_Api_Success_Res<T_Api_Paginated<EmailThread>> | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            result: {
              ...oldData.result,
              data: oldData.result.data.map((thread) =>
                thread.externalId === threadId ? { ...thread, hasRead: true } : thread,
              ),
            },
          };
        },
      );
    };
  },
  useGetInfiniteEmails: ({ search, ...filters }: PaginationProps, activator: (params: PaginationProps) => boolean) => {
    const queryClient = useQueryClient();
    const debouncedSearch = useDebounce(search, 500);

    return useQuery({
      queryKey: ["emails", { ...filters, search: debouncedSearch }],
      queryFn: async () => {
        const { data } = await api.get<T_Api_Success_Res<T_Api_Paginated<EmailThread>>>("/mail/threads", {
          params: {
            populate_creditors: true,
            page: filters.page,
            limit: filters.limit,
            search: debouncedSearch,
            type: filters.type,
            matchEmails: filters.matchEmails,
            appKey: filters.appKey,
            loanId: filters.loanId,
          },
        });

        return data;
      },
      enabled: () => activator({ search: debouncedSearch, ...filters }),
      refetchInterval: () => {
        if (!filters.profileId) return 30 * 1000;
        const profiles = queryClient.getQueryData<T_Api_Success_Res<EmailAccountConfig[]>>([
          "attached-profiles",
          filters.appKey,
        ]);
        const isSyncing = profiles?.result.find((p) => p._id === filters.profileId)?.isSyncing;
        return (isSyncing ? 5 : 30) * 1000;
      },
    });
  },
  useGetInfiniteEmailsForOrg: (
    { search, ...filters }: PaginationProps,
    activator: (params: PaginationProps) => boolean,
  ) => {
    const queryClient = useQueryClient();
    const debouncedSearch = useDebounce(search, 500);

    return useQuery({
      queryKey: ["org-emails", { ...filters, search: debouncedSearch }],
      queryFn: async () => {
        const { data } = await api.get<T_Api_Success_Res<T_Api_Paginated<EmailThread>>>("/organisation/mail/threads", {
          params: {
            populate_creditors: true,
            page: filters.page,
            limit: filters.limit,
            search: debouncedSearch,
            type: filters.type,
            tags: filters.tags,
            matchEmails: filters.matchEmails,
            profileId: filters.profileId,
            orgCode: filters.orgCode,
            appKey: filters.appKey,
            loanId: filters.loanId,
          },
        });

        return data;
      },
      enabled: () => activator({ search: debouncedSearch, ...filters }),
      refetchInterval: () => {
        if (!filters.profileId) return 30 * 1000;
        const profiles = queryClient.getQueryData<T_Api_Success_Res<EmailAccountConfig[]>>([
          "attached-profiles",
          filters.appKey,
        ]);
        const isSyncing = profiles?.result.find((p) => p._id === filters.profileId)?.isSyncing;
        return (isSyncing ? 5 : 30) * 1000;
      },
    });
  },
  // useGetInbox: (appKey: string | undefined) => {
  //   return useQuery({
  //     queryKey: ["get-email-thread", appKey],
  //     queryFn: async () => {
  //       const res = await api.get(`/mail/threads?appKey=${appKey}`);
  //       return res.data as T_Api_Success_Res<T_Api_Paginated<EmailThread>>;
  //     },
  //     enabled: !!appKey,
  //   });
  // },
  // useGetThreadMessages: ({
  //   appKey,
  //   threadId,
  //   limit,
  //   page,
  // }: {
  //   threadId: string | undefined;
  //   appKey: string | undefined;
  //   page: number;
  //   limit: number;
  // }) => {
  //   const markAsRead = emailService.useMarkThreadAsRead();

  //   return useQuery({
  //     queryKey: ["get-thread-messages", threadId, appKey, page, limit],
  //     queryFn: async () => {
  //       const res = await api.get(`/mail/threads/${threadId}/messages?appKey=${appKey}`, {
  //         params: {
  //           page,
  //           limit,
  //         },
  //       });

  //       markAsRead(threadId!);
  //       return res.data as T_Api_Success_Res<T_Api_Paginated<EmailThreadData>>;
  //     },
  //     enabled: !!threadId && !!appKey,
  //   });
  // },
  useGetThreadMessagesInfinite: ({
    appKey,
    threadId,
    limit,
  }: {
    threadId: string | undefined;
    appKey: string | undefined;
    limit: number;
  }) => {
    const markAsRead = emailService.useMarkThreadAsRead();

    return useInfiniteQuery({
      queryKey: ["get-thread-messages", threadId, appKey, limit],
      enabled: !!threadId && !!appKey,
      initialPageParam: 1,

      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await api.get<T_Api_Success_Res<T_Api_Paginated<EmailThreadData>>>(
          `/mail/threads/${threadId}/messages`,
          {
            params: {
              page: pageParam,
              limit,
              appKey,
            },
          },
        );

        if (threadId) markAsRead(threadId);

        return data.result;
      },

      getNextPageParam: (lastPage) => lastPage?.nextPage,
    });
  },
  useGetThreadMessagesInfiniteForOrg: ({
    orgCode,
    threadId,
    limit,
    profileId,
  }: {
    threadId: string | undefined;
    orgCode: string | undefined;
    limit: number;
    profileId?: string;
  }) => {
    const markAsRead = emailService.useMarkThreadAsRead();

    return useInfiniteQuery({
      queryKey: ["get-thread-messages", threadId, orgCode, limit, profileId],
      enabled: Boolean(threadId) && Boolean(orgCode) && Boolean(profileId),
      initialPageParam: 1,

      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await api.get<T_Api_Success_Res<T_Api_Paginated<EmailThreadData>>>(
          `/organisation/mail/threads/${threadId}/messages`,
          {
            params: {
              page: pageParam,
              limit,
              profileId,
              orgCode,
            },
          },
        );

        if (threadId) markAsRead(threadId);

        return data.result;
      },

      getNextPageParam: (lastPage) => lastPage?.nextPage,
    });
  },
  useEditThread: () => {
    return useMutation({
      mutationFn: async ({ _id: threadId, ...payload }: Partial<EmailThread> & Pick<EmailThread, "_id">) => {
        // Accepts both _id and externalID
        const { data } = await api.patch(`/mail/threads/${threadId}`, payload);
        return data;
      },
    });
  },

  useReplyEmail: () => {
    return useMutation({
      mutationKey: ["reply-email"],
      mutationFn: async ({
        messageId,
        appKey,
        profileId,
        ...payload
      }: SendEmailPayload & {
        messageId: string;
        appKey: string;
        profileId?: string;
        negotiation_id: string | null;
      }) => {
        const formData = new FormData();
        for (const f of payload.attachments) {
          formData.append("files", f);
        }

        formData.set("subject", payload.subject);
        formData.set("body", payload.body);
        if (payload.negotiation_id) {
          formData.set("negotiation_id", payload.negotiation_id);
        }
        if (payload.to?.length) {
          formData.set("to", JSON.stringify(payload.to));
        }
        if (payload.cc?.length) {
          formData.set("cc", JSON.stringify(payload.cc));
        }
        if (payload.bcc?.length) {
          formData.set("bcc", JSON.stringify(payload.bcc));
        }

        let url = "";
        if (appKey) {
          url = `/mail/reply/${messageId}?appKey=${appKey}`;
        } else if (profileId) {
          url = `/mail/reply/${messageId}?profileId=${profileId}`;
        }

        url += "&fixed=true";

        const res = await api.post(url, formData);
        return res.data;
      },
    });
  },

  useAttachLoanToThread: () => {
    return useMutation({
      mutationKey: ["thraed-loan-attach"],
      mutationFn: async ({ loanId, threadId }: { threadId: string; loanId: string }) => {
        const res = await api.patch(`/mail/threads/${threadId}/${loanId}`);
        return res.data;
      },
    });
  },

  useGetEmailSuggestions: ({
    appKey,
    page,
    limit,
    search,
  }: {
    appKey: string | undefined;
    page: number;
    limit: number;
    search: string;
  }) => {
    const debouncedSearch = useDebounce(search, 500);
    return useQuery({
      queryKey: ["email-suggestions", appKey, page, limit, debouncedSearch],
      queryFn: async () => {
        const res = await api.get(`/mail/suggestions`, {
          params: {
            appKey,
            page,
            limit,
            search: debouncedSearch,
          },
        });
        return res.data as T_Api_Success_Res<T_Api_Paginated<string>>;
      },
      enabled: !!appKey,
    });
  },
  useGetAttachedProfiles: ({ appKey }: { appKey: string | undefined }) => {
    return useQuery({
      queryKey: ["attached-profiles", appKey],
      queryFn: async () => {
        const res = await api.get(`/mail/profiles?appKey=${appKey}`);
        return res.data as T_Api_Success_Res<EmailAccountConfig[]>;
      },
      enabled: !!appKey,
      refetchInterval: 20 * 1000,
    });
  },
  useGetOrganisationsProfiles: ({ orgCode }: { orgCode: string | undefined }) => {
    return useQuery({
      queryKey: ["org-attached-profiles", orgCode],
      queryFn: async () => {
        const res = await api.get(`/organisation/profiles?orgCode=${orgCode}`);
        return res.data as T_Api_Success_Res<EmailAccountConfig[]>;
      },
      enabled: !!orgCode,
      refetchInterval: 20 * 1000,
    });
  },
  useUpdateProfileACL: () => {
    return useMutation({
      mutationFn: async ({ id, acl }: { id: T_MongoId; acl: ProfileACL }) => {
        const payload = { profileId: id, ...acl };
        for (const capability of Object.values(ProfileAclCapabilities)) {
          payload[capability].users = payload[capability].users.map(
            (user) => user._id,
          ) as unknown as ProfileAclTargets["users"];
        }

        const { data } = await api.put<T_Api_Success_Res<unknown>>("/mail/profiles/acl", payload);
        return data;
      },
    });
  },
  useSwitchProfile: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["switch/profile"],
      mutationFn: async ({ profileId, appKey }: { profileId?: string; appKey?: string }) => {
        const res = await api.patch(
          `/mail/profiles/switch/${profileId}`,
          {},
          {
            params: {
              appKey,
            },
          },
        );
        return res.data;
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["emails"] });
      },
    });
  },
  useSwitchDefaultSenderProfile: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["switch/default-sender/profile"],
      mutationFn: async ({ profileId, orgCode }: { profileId?: string; orgCode?: string }) => {
        const res = await api.patch(
          `/organisation/profiles/sender/${profileId}`,
          {},
          {
            params: {
              orgCode,
            },
          },
        );
        return res.data;
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["org-attached-profiles"] });
      },
    });
  },
  useSwitchOrgProfile: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["switch/profile/org"],
      mutationFn: async ({ profileId, orgCode }: { profileId?: string; orgCode?: string }) => {
        const res = await api.patch(
          `/organisation/profiles/switch/${profileId}`,
          {},
          {
            params: {
              orgCode,
            },
          },
        );
        return res.data;
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["emails"] });
      },
    });
  },
  useDisconnectProfile: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async ({ profileId, appKey }: { profileId: string; appKey: string | undefined }) => {
        const res = await api.delete<T_Api_Success_Res<{ selected: string | null }>>(`/mail/profiles/${profileId}`, {
          params: {
            appKey,
          },
        });
        return res.data;
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["attached-profiles"] });
        queryClient.invalidateQueries({ queryKey: ["emails"] });
      },
    });
  },
  useDisconnectOrgProfile: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async ({ profileId, orgCode }: { profileId: string; orgCode: string | undefined }) => {
        const res = await api.delete<T_Api_Success_Res<{ selected: string | null }>>(
          `/organisation/profiles/${profileId}`,
          {
            params: {
              orgCode,
            },
          },
        );
        return res.data;
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["org-attached-profiles"] });
        queryClient.invalidateQueries({ queryKey: ["emails"] });
      },
    });
  },
  useGetSyncQueue: ({ page, limit, search: searchText }: { page: number; limit: number; search?: string }) => {
    const search = useDebounce(searchText, 500);
    const params = { page, limit, search };

    return useQuery({
      queryKey: ["mail-sync", params],
      queryFn: async () => {
        type MailSync = T_Api_Success_Res<
          T_Api_Paginated<{
            profile: ProfileDto;
            syncTill?: Date;
            nextSyncAt?: Date;
            errors?: number;
          }>
        >;

        const { data } = await api.get<MailSync>(`/mail/sync`, { params });
        return data;
      },
      refetchInterval: 10 * 1000,
    });
  },
  useSyncEmails: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: ["sync-email"],
      mutationFn: async ({
        profileId,
        appKey,
        startDate,
        endDate,
      }: {
        profileId: string;
        appKey?: string;
        startDate: Dayjs | null;
        endDate: Dayjs | null;
      }) => {
        const res = await api.post(
          `/mail/sync`,
          {},
          {
            params: {
              profileId: profileId,
              appKey: appKey,
              startDate: startDate ? startDate?.format("YYYY-MM-DDTHH:mm:ss") : undefined,
              endDate: endDate
                ? endDate
                    ?.set("hour", 23)
                    .set("minute", 59)
                    .set("second", 59)
                    ?.set("millisecond", 999)
                    ?.format("YYYY-MM-DDTHH:mm:ss")
                : undefined,
            },
          },
        );

        return res.data;
      },
      onSuccess: (_, { profileId, appKey }) => {
        queryClient.setQueryData<T_Api_Success_Res<EmailAccountConfig[]>>(["attached-profiles", appKey], (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            result: prev.result.map((profile) =>
              profile._id === profileId ? { ...profile, isSyncing: true } : profile,
            ),
          };
        });
      },
    });
  },
  useGetThreadFilter: ({ profileId }: { profileId: string | undefined }) => {
    return useQuery({
      queryKey: ["get-thread-filters"],
      queryFn: async () => {
        const res = await api.get(`/mail/thread-filters`, {
          params: { profileId },
        });
        return res.data as T_Api_Success_Res<{
          creditors: Creditor_Filter[];
          applications: DebtorFilter[];
        }>;
      },
      enabled: !!profileId,
    });
  },
  useUpsertDraft: () => {
    return useMutation({
      mutationKey: ["mail", "threads", "drafts", "upsert"],

      mutationFn: async ({
        threadId,
        body,
      }: {
        threadId: string;
        body: {
          messageId?: string;
          profileId?: string;
          subject?: string;
          replyMessage: string;
          to?: string[];
          cc?: string[];
          bcc?: string[];
        };
      }) => {
        const res = await api.post(`/mail/threads/${threadId}/drafts`, body);

        return res.data;
      },
    });
  },
  useGetDraft: ({ threadId }: { threadId?: string }) => {
    return useQuery({
      queryKey: ["mail", "threads", threadId, "draft"],
      queryFn: async () => {
        const res = await api.get(`/mail/threads/${threadId}/drafts`);

        return res.data as T_Api_Success_Res<DraftDto>;
      },
      enabled: !!threadId,
    });
  },
  useDeleteDraft: () => {
    return useMutation({
      mutationKey: ["mail", "threads", "drafts", "delete"],

      mutationFn: async ({ threadId, draftId }: { threadId: string; draftId: string }) => {
        const res = await api.delete(`/mail/threads/${threadId}/drafts/${draftId}`);

        return res.data;
      },
    });
  },
  useGetAprCharts: (appKey?: string) => {
    return useQuery({
      queryKey: ["get-apr-charts", appKey],
      queryFn: async () => {
        const res = await api.get(`/risk-plus/application/${appKey}/apr-summary/charts`, {
          headers: {
            Authorization: `basic YjM5MmQyNDA5ODYyYTk3NjpkNzU4NGY2MjhjMWJjZjEwNzJiMjI0ODJlN2IzZTE0Yg==`,
          },
        });
        return res.data as T_Api_Success_Res<{ charts: AprChart[] }>;
      },
      enabled: !!appKey,
    });
  },
};
